// Ein Passkey im Browser: die zwei Handgriffe, ohne JSX daneben.
//
// ORIGINALTEXT MARVIN (03.09.2026)
//   „ja, baue Passkey (WebAuthn) auch noch daneben."
//
// SIE STEHEN OHNE JSX, aus demselben Grund wie `namen.ts`, `terminraster.ts`
// und `kartenmass.ts`: eine Datei mit JSX lässt sich mit
// `--experimental-strip-types` nicht laden, und dann bliebe einem Prüfstand
// nur die Textsuche - **eine Textsuche misst, dass etwas dasteht, nicht, dass
// es stimmt.**
//
// WAS HIER NICHT STEHT, IST DIE PRÜFUNG. Der Browser liefert eine
// Unterschrift; ob sie gilt, entscheidet der Server. Eine Prüfung im Browser
// wäre eine Anzeige und kein Riegel - dieselbe Regel wie überall in diesem
// Haus.

/** Base64url, wie der Server sie schickt und erwartet. */
export function ausB64u(text: string): Uint8Array<ArrayBuffer> {
  const t = (text ?? '').replace(/-/g, '+').replace(/_/g, '/');
  const roh = atob(t + '='.repeat((4 - (t.length % 4)) % 4));
  /* AUF EINEM EIGENEN PUFFER. `new Uint8Array(n)` allein trägt für den
     Übersetzer einen `ArrayBufferLike`, und der könnte geteilt sein - die
     Schnittstelle des Browsers nimmt nur einen eigenen an. Ein `as any`
     daneben hätte es auch getan und die Frage verdeckt. */
  const puffer = new ArrayBuffer(roh.length);
  const raus = new Uint8Array(puffer);
  for (let i = 0; i < roh.length; i++) raus[i] = roh.charCodeAt(i);
  return raus;
}

export function b64u(roh: ArrayBuffer): string {
  const b = new Uint8Array(roh);
  let s = '';
  for (let i = 0; i < b.length; i++) s += String.fromCharCode(b[i]);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Kann dieser Browser überhaupt Passkeys? */
export function passkeyMoeglich(): boolean {
  return typeof window !== 'undefined'
      && !!(window as any).PublicKeyCredential
      && !!navigator.credentials?.create;
}

/**
 * Warum es NICHT ging.
 *
 * Der Browser wirft für sechs verschiedene Lagen dieselbe Sorte Fehler, und
 * `NotAllowedError` heisst sowohl „abgebrochen" als auch „Zeit abgelaufen"
 * als auch „dieses Gerät will nicht". **Ein Statuswort ist keine Antwort** -
 * hier steht, was ein Mensch als nächstes tun kann.
 */
export function passkeyFehler(f: any): string {
  const name = String(f?.name ?? '');
  if (name === 'NotAllowedError') {
    return 'Abgebrochen oder zu lange gewartet. Noch einmal versuchen - der '
         + 'Browser fragt dann wieder nach Fingerabdruck, Gesicht oder PIN.';
  }
  if (name === 'InvalidStateError') {
    return 'Auf diesem Gerät ist für diesen Zugang schon ein Passkey '
         + 'eingerichtet. Er steht in der Liste darunter.';
  }
  if (name === 'NotSupportedError') {
    return 'Dieses Gerät kann kein Verfahren, das hier angenommen wird.';
  }
  if (name === 'SecurityError') {
    return 'Die Adresse im Browser passt nicht zu dieser Anlage. Ein Passkey '
         + 'ist an die Adresse gebunden - das ist der ganze Sinn.';
  }
  return String(f?.message ?? 'Es hat nicht geklappt.');
}

/**
 * Einen Passkey anlegen.
 *
 * `authenticatorSelection` bleibt WEIT: dieses Haus verlangt weder ein
 * eingebautes Gerät noch einen Sicherheitsstick. Wer einen Stick benutzen
 * will, soll es können; wer den Fingerabdruck seines Telefons nimmt, auch.
 * Eine Einschränkung hier nimmt jemandem den Weg, ohne dass sie etwas
 * sicherer macht.
 *
 * `attestation: 'none'` mit Absicht: eine Bescheinigung, WELCHES Gerät das
 * ist, wird hier nicht geprüft und deshalb auch nicht verlangt. Sie zu holen
 * und nicht zu prüfen wäre die Sorte Gründlichkeit, die nur wie eine aussieht.
 */
export async function passkeyAnlegen(start: {
  aufgabe: string;
  rp: { id: string; name: string };
  benutzer: { id: string; name: string; anzeige: string };
  verfahren: number[];
  schon: string[];
}): Promise<{ kennung: string; clientDaten: string; attestation: string }> {
  const c = (await navigator.credentials.create({
    publicKey: {
      challenge: ausB64u(start.aufgabe),
      rp: { id: start.rp.id, name: start.rp.name },
      user: {
        id: ausB64u(start.benutzer.id),
        name: start.benutzer.name,
        displayName: start.benutzer.anzeige,
      },
      pubKeyCredParams: start.verfahren.map(
        (alg) => ({ type: 'public-key' as const, alg })),
      /* SCHON VORHANDENE GEHEN MIT. Ohne sie legt dasselbe Gerät beim zweiten
         Mal einen zweiten Passkey an, und in der Liste stehen zwei Zeilen,
         die dasselbe Telefon meinen. */
      excludeCredentials: start.schon.map((k) => ({
        type: 'public-key' as const, id: ausB64u(k),
      })),
      authenticatorSelection: { userVerification: 'preferred' },
      attestation: 'none',
      timeout: 120_000,
    },
  })) as PublicKeyCredential | null;
  if (!c) throw new Error('Der Browser hat keinen Schlüssel geliefert.');
  const a = c.response as AuthenticatorAttestationResponse;
  return {
    kennung: b64u(c.rawId),
    clientDaten: b64u(a.clientDataJSON),
    attestation: b64u(a.attestationObject),
  };
}

/** Sich mit einem Passkey ausweisen. */
export async function passkeyAntworten(start: {
  aufgabe: string; rpId: string; schluessel: string[];
}): Promise<{ kennung: string; clientDaten: string; authDaten: string;
              unterschrift: string }> {
  const c = (await navigator.credentials.get({
    publicKey: {
      challenge: ausB64u(start.aufgabe),
      rpId: start.rpId,
      allowCredentials: start.schluessel.map((k) => ({
        type: 'public-key' as const, id: ausB64u(k),
      })),
      userVerification: 'preferred',
      timeout: 120_000,
    },
  })) as PublicKeyCredential | null;
  if (!c) throw new Error('Der Browser hat keine Unterschrift geliefert.');
  const a = c.response as AuthenticatorAssertionResponse;
  return {
    kennung: b64u(c.rawId),
    clientDaten: b64u(a.clientDataJSON),
    authDaten: b64u(a.authenticatorData),
    unterschrift: b64u(a.signature),
  };
}
